import { MAX_LICENSE_COUNT, billingTypes, getMinLicenseCount } from '@coursera-web/wes-common';
import type { LocationDescriptorObject } from 'history';
import moment from 'moment';

import type { InjectedRouter } from 'js/lib/connectToRouter';
import { setLocation } from 'js/lib/coursera.redirect';
import type { NavigateFunction } from 'js/lib/useRouter';
import user from 'js/lib/user';

import { errorMapping } from 'bundles/payments-common/common/constants';
import {
  getErrorMessageForHumanReadableCode,
  getErrorMessageForPaymentProcessorCode,
  getErrorTitle,
} from 'bundles/payments-common/components/errorMessages';
import { logOutUser } from 'bundles/teams-v2/api/logOutUser';
import { ANNUALLY, QUARTERLY } from 'bundles/teams-v2/constants/billing';

// i18n
import _t from 'i18n!nls/teams-v2';

export const getQueryFromRouter = (router: InjectedRouter, queryKey: string): string => {
  if (queryKey === 'learnerCount') {
    throw new Error(
      'It is unsafe to retrieve learnerCount through this utility function, use getLearnerCountFromRouter.'
    );
  }

  return router?.location?.query?.[queryKey];
};

export const getQueryFromLocation = (location: LocationDescriptorObject, queryKey: string): string | undefined => {
  if (queryKey === 'learnerCount') {
    throw new Error(
      'It is unsafe to retrieve learnerCount through this utility function, use getLearnerCountFromRouter.'
    );
  }

  return location?.query?.[queryKey];
};

export const getLearnerCountFromLocation = (location: LocationDescriptorObject) => {
  const queryKey = 'learnerCount';

  // Prevents URL manipulation
  return (location && location.query && location.query[queryKey]) || null;
};

/**
 * Safely extracts and validates learner count from URL query parameters
 *
 * @param location - The location object containing query parameters
 * @returns Validated learner count number or null if invalid/missing
 *
 * Validation rules:
 * - Returns null for non-numeric, missing, or invalid values (including negative, zero, decimals)
 * - For valid numbers: clamps values below effective min (see {@link getMinLicenseCount}) to that min
 * - For valid numbers: clamps values above MAX_LICENSE_COUNT (125) to MAX_LICENSE_COUNT
 */
export const getValidatedLearnerCountFromLocation = (location: LocationDescriptorObject): number | null => {
  const minLicenseCount = getMinLicenseCount(false);
  const rawValue = getLearnerCountFromLocation(location);

  // Return null for missing or non-string values
  if (!rawValue || typeof rawValue !== 'string') {
    return null;
  }

  // Parse the value
  const parsedValue = parseInt(rawValue, 10);

  // Return null for non-numeric values (NaN, Infinity, etc.)
  if (!Number.isFinite(parsedValue)) {
    return null;
  }

  // Return null for invalid values (negative, zero)
  if (parsedValue <= 0) {
    return null;
  }

  // Clamp valid numbers to valid range
  if (parsedValue < minLicenseCount) {
    return minLicenseCount;
  }

  if (parsedValue > MAX_LICENSE_COUNT) {
    return MAX_LICENSE_COUNT;
  }

  return parsedValue;
};

/**
 * Safely extracts and validates billing type from URL query parameters.
 * Valid values are ANNUALLY (1) and QUARTERLY (4). Defaults to ANNUALLY for invalid/missing values.
 */
export const getValidatedBillingTypeFromLocation = (location: LocationDescriptorObject): number => {
  const rawValue = location?.query?.billingType;

  if (!rawValue || typeof rawValue !== 'string') {
    return ANNUALLY;
  }

  const parsed = parseInt(rawValue, 10);

  if (parsed === ANNUALLY || parsed === QUARTERLY) {
    return parsed;
  }

  return ANNUALLY;
};

export const getBillingType = (billingType: number) => {
  switch (billingType) {
    case ANNUALLY:
    default:
      return billingTypes.ONE_TIME;
    case QUARTERLY:
      return billingTypes.QUARTERLY;
  }
};

export const getLearnerCountFromRouter = (router: InjectedRouter) => {
  return getQueryFromRouter(router, 'learnerCount');
};

export const isCheckingOutForExistingOrg = (location: LocationDescriptorObject) => {
  const thirdPartyOrganizationId = getQueryFromLocation(location, 'thirdPartyOrganizationId');
  return user.isAuthenticatedUser() && thirdPartyOrganizationId;
};

export const planAutoRenewDate = moment().add(1, 'year').calendar();

// Free trial start date = (now@UTC + 15 days).round to start of day
export const calculateFreeTrialBillingStartDate = () => moment(Date.now()).utc().add(15, 'days').startOf('day').local();

export function getRegistrationErrorMessage(code: string): string {
  const errorMap: Record<string, string> = {
    malformedEmailAddress: _t('The email address you entered is not valid.'),
    failedPasswordRequirements: _t('Password doesn’t meet specified validation criteria.'),
    invalidName: _t(`The name is not valid`),
  };

  return errorMap[code] || 'An unexpected error has occurred. Please try again';
}

/**
 * Updates URL parameters and navigates
 * @param navigate - Navigation function from useNavigate
 * @param pathname - Target path
 * @param query - Query parameters to include
 * @param options - Navigation options (defaults to { replace: true })
 */
export const updateURLParams = (
  navigate: NavigateFunction,
  pathname: string,
  query: Record<string, string | number | undefined>,
  options: { replace?: boolean } = { replace: true }
): void => {
  const searchParams = new URLSearchParams(window.location.search);

  Object.entries(query).forEach(([key, value]) => {
    if (value == null) {
      searchParams.delete(key);
    } else {
      searchParams.set(key, String(value));
    }
  });

  const search = searchParams.toString();
  navigate(
    {
      pathname,
      search: search ? `?${search}` : '',
    },
    options
  );
};

/**
 * Logs out the current user and redirects them to a specified path.
 * This function is typically used when a user needs to be logged out and redirected
 * to a different page, such as during account switching or invalid session scenarios.
 *
 * @param {string} path - The URL path to redirect to after logout
 * @returns {Promise<void>} A promise that resolves when the logout and redirect are complete
 *
 * @example
 * // Log out user and redirect to signup page
 * await logOutAndRedirectTo('/purchase/plan');
 */
export const logOutAndRedirectTo = async (path: string) => {
  await logOutUser();

  // Redirecting user to the path
  setLocation(path);
};

export type ErrorMessageStructure = {
  title?: string;
  content: string | JSX.Element;
};

/**
 * Gets structured error message with optional title and content
 * Returns { title?, content } where title is used for notification header
 */
export const getStructuredErrorMessage = (
  submissionErrorCode: string | undefined,
  submissionErrorDetails?: {
    processorResponseText?: string;
    processorResponseCode?: string;
  }
): ErrorMessageStructure => {
  let content: string | JSX.Element | undefined;
  let title: string | undefined;

  // Priority 1: Stripe-specific error with processor response text
  if (submissionErrorCode === errorMapping.stripeError && submissionErrorDetails?.processorResponseText) {
    content = submissionErrorDetails.processorResponseText;
  }
  // Priority 2: Payment processor code error message (numeric codes like 2004, 2015, etc.)
  else if (submissionErrorDetails?.processorResponseCode) {
    content = getErrorMessageForPaymentProcessorCode(submissionErrorDetails.processorResponseCode);
  }
  // Priority 3: Human-readable error message (coded errors like 'card-declined-error', 'paypal-error', etc.)
  else if (submissionErrorCode) {
    content = getErrorMessageForHumanReadableCode(submissionErrorCode, undefined, true);
  }

  // If we found content, check if there's a custom title for this error code
  if (content && submissionErrorCode) {
    title = getErrorTitle(submissionErrorCode);
  }

  // Priority 4: Fallback to default payment failed message
  if (!content) {
    content = _t("We couldn't process your payment. Please check your card details or try another method.");
  }

  return { title, content };
};

/**
 * Navigates to a new path while preserving the current query parameters.
 * @param navigate - Navigation function from useNavigate
 * @param pathname - Target path
 * @returns {void}
 */
export const navigatePreserveQuery = (navigate: NavigateFunction, pathname: string) => {
  navigate({
    pathname,
    search: window.location.search,
  });
};
